/**
 * A dropdown that takes several values (Dax, 2026-10-01): it looks like the selects beside it, opens on a
 * list of ticks grouped as those selects group their options, and ends in a box that adds a new value.
 * The tag modal uses it for every category that may hold more than one value on a clip.
 */
import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import { groupFacetValues } from '@studio/contracts';
import { Chevron } from '@/components/icons';

export function MultiSelect({ label, options, value, onChange }: {
  label: string;
  options: readonly string[];
  value: readonly string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const has = (v: string) => value.some((x) => x.toLowerCase() === v.toLowerCase());
  // A value already chosen but no longer offered (or just added) still shows, ticked.
  const all = [...options, ...value.filter((v) => !options.some((o) => o.toLowerCase() === v.toLowerCase()))];
  const groups = groupFacetValues(all);
  const flip = (v: string) => onChange(has(v) ? value.filter((x) => x.toLowerCase() !== v.toLowerCase()) : [...value, v]);
  const addNew = () => {
    const v = draft.trim();
    if (v && !has(v)) onChange([...value, v]);
    setDraft('');
  };
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className="multi-trigger" aria-label={label} aria-haspopup="listbox">
          <span className={`multi-value${value.length ? '' : ' none'}`}>{value.length ? value.join(', ') : 'No tag'}</span>
          <Chevron />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="multi-pop" align="start" sideOffset={4}
          // Escape closes this list only, never the modal it sits in.
          onEscapeKeyDown={(e) => e.stopPropagation()}>
          <div className="multi-list" role="listbox" aria-label={label} aria-multiselectable="true">
            {groups.map((g) => (
              <div key={g.label} className="multi-group">
                {groups.length > 1 && <span className="label">{g.label}</span>}
                {g.values.map((v) => (
                  <button key={v} type="button" role="option" aria-selected={has(v)} className="tick multi-opt" onClick={() => flip(v)}>
                    <i className={has(v) ? 'on' : ''}>{has(v) && <svg viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg>}</i>
                    {v}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <input className="multi-new" value={draft} placeholder="Add new…" maxLength={120} aria-label={`New ${label}`}
            onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNew(); } }} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
